import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@amzn/awsui-components-react/polaris/table';
import Header from '@amzn/awsui-components-react/polaris/header';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import { Shift } from '@amzn/red-velvet-api';
import { usePropertyFilterStrings } from '../../hooks/localization/propertyFilterStrings';
import { TablePropertyFilter } from '../attendance/wizard/tablePropertyFilter';
import { usePaginationStrings } from '../../hooks/localization/paginationStrings';
import { PeoplePreferences } from '../attendance/wizard/peoplePreferences';
import { useTableColumns } from '../../hooks/tableColumns';
import {
  AmazonSignup,
  useSignupTableCollection,
  useSignupFilterProperties,
  useBaseSignupColumnMap,
  INITIAL_SIGNUP_PAGE_SIZE,
} from './signupTableCommon';

interface SignupSheetForShiftProps {
  signups: AmazonSignup[];
  isLoading: boolean;
  isError: boolean;
  shift?: Shift;
}

export function SignupTableForShift({
  signups,
  isLoading,
  isError,
  shift,
}: SignupSheetForShiftProps) {
  const { t } = useTranslation('signup');
  const [pageSize, setPageSize] = useState(INITIAL_SIGNUP_PAGE_SIZE);
  const paginationStrings = usePaginationStrings('signupSheet', ['signup']);
  const propertyFilterStrings = usePropertyFilterStrings('propertyFilter', ['signup']);

  const columnMap = useBaseSignupColumnMap();
  const { columnDefinitions } = useTableColumns(columnMap, 'SignupSheetTableForShift', ['signup']);
  const filteringProperties = useSignupFilterProperties();

  const { items, collectionProps, propertyFilterProps, paginationProps } = useSignupTableCollection(
    signups,
    filteringProperties,
    isError,
    pageSize
  );

  return (
    <Table
      {...collectionProps}
      columnDefinitions={columnDefinitions}
      items={items}
      loading={isLoading}
      trackBy="signupId"
      header={
        <Header counter={`(${signups.length})`}>{shift?.name || t('signupSheet.title')}</Header>
      }
      filter={
        <TablePropertyFilter
          {...propertyFilterProps}
          propertyFilterStrings={propertyFilterStrings}
          filteringPlaceholder={t('signupSheet.filteringPlaceholder')}
        />
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationStrings} />}
      preferences={
        <PeoplePreferences
          onConfirm={({ detail }) => setPageSize(detail.pageSize || 100)}
          preferences={{ pageSize }}
        />
      }
    />
  );
}
